<template>
  <div>
    <SetPopup ref="popup" />

    <!-- 알림 다이얼로그 -->
    <v-dialog v-model="alertDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="headline">알림</v-card-title>
        <v-card-text>
          <div style="white-space: pre-line">{{ alertMessage }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="handleAlertConfirm">확인</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="service">
      <div class="btn-area">
        <v-card-actions class="mr-0">
          <v-btn depressed class="btn-reset" @click="cancel">취소</v-btn>
        </v-card-actions>
        <v-card-actions>
          <v-btn depressed class="btn-search" @click="save">저장</v-btn>
        </v-card-actions>
      </div>
      <RealGrid :domName="grid" ref="grid" :settings="settings" />
    </div>
  </div>
</template>

<script>
import { columns, fields, loadCategoryData, updateCategoryColumn } from '@/assets/grid/materialPropertyAdd';
import SetPopup from '@/components/SetPopup.vue';
import RealGrid from '@/components/RealGrid.vue';
import { materialPropertyInsert } from 'api/materialProperty/materialProperty';
import { mapMutations, mapState } from 'vuex';

export default {
  data() {
    return {
      isFetching: false,
      grid: 'materialPropertyAdd',
      settings: {
        columns,
        fields,
        height: 400,
        hideCheckBar: false, // 체크박스 표시
        errorMessage: '등록할 원료 특성 데이터를 입력해주세요',
      },
      gridData: [],
      alertDialog: false,
      alertMessage: '',
      alertCallback: null,
    };
  },

  computed: {
    ...mapState('member', ['userInfo']),
  },

  async mounted() {
    // Account.vue 방식으로 팝업 설정
    this.SET_POPUP({
      title: '알림',
      width: 300,
      customApprove: true,
    });

    // 카테고리 데이터 로드 및 그리드 초기화
    await this.loadCategories();

    // Account.vue 방식처럼 지연 후 그리드 초기화
    setTimeout(() => {
      this.initializeGrid();
    }, 300);
  },

  methods: {
    ...mapMutations('popup', ['SET_POPUP', 'SET_POPUP_TEXT']),

    async loadCategories() {
      try {
        // 카테고리 데이터 로드
        await loadCategoryData();
        // 컬럼 업데이트
        updateCategoryColumn();
        // 그리드가 이미 초기화되어 있다면 런타임으로 컬럼 옵션 반영
        this.$nextTick(() => {
          this.applyCategoryOptionsToGrid();
        });
      } catch (error) {
        console.error('카테고리 로드 실패:', error);
      }
    },

    initializeGrid() {
      // Account.vue 방식으로 빈 행 1개 추가
      const today = new Date().toISOString().slice(0, 10);
      const currentUser = this.userInfo.member_name || '';

      const emptyRow = {
        category: '',
        material: '',
        mw: '',
        density: '',
        remark: '',
        reg_date: today,
        reg_user: currentUser,
      };

      this.gridData = [emptyRow];

      // Account.vue 방식으로 데이터 로드
      if (this.$refs.grid && typeof this.$refs.grid.loadData === 'function') {
        this.$refs.grid.loadData(this.gridData);
      }

      // 그리드 초기화 직후 카테고리 드롭다운 옵션을 안전하게 주입
      this.$nextTick(() => {
        this.applyCategoryOptionsToGrid();
      });
    },

    // RealGrid의 'category' 컬럼에 values/labels을 런타임으로 주입해서 드롭다운이 비어 보이는 문제를 방지
    applyCategoryOptionsToGrid() {
      try {
        const gridRef = this.$refs.grid;
        if (!gridRef || !gridRef.gv) return;
        const gv = gridRef.gv;

        // 설정에서 최신 values/labels을 가져옴 (loadCategoryData + updateCategoryColumn 이후 columns가 갱신됨)
        const categoryColCfg = (this.settings.columns || []).find(col => col.fieldName === 'category');
        if (!categoryColCfg) return;

        const values = Array.isArray(categoryColCfg.values) ? categoryColCfg.values : [];
        const labels = Array.isArray(categoryColCfg.labels) ? categoryColCfg.labels : [];

        if (values.length === 0 || labels.length === 0) return;

        // 런타임으로 컬럼 속성 주입
        gv.setColumnProperty('category', 'values', values);
        gv.setColumnProperty('category', 'labels', labels);
        gv.setColumnProperty('category', 'lookupDisplay', true);
        gv.setColumnProperty('category', 'editButtonVisibility', 'always');
        gv.setColumnProperty('category', 'editor', {
          type: 'dropdown',
          textReadOnly: true,
          domainOnly: true,
          dropDownWhenClick: true,
        });

        gv.refresh();
      } catch (e) {
        console.error('카테고리 드롭다운 옵션 주입 실패:', e);
      }
    },

    validate() {
      const rows = this.$refs.grid.getJsonRows();
      const errors = [];

      rows.forEach((row, index) => {
        const rowNum = index + 1;

        // 필수 필드 검증
        if (!row.category || row.category.trim() === '') {
          errors.push(`구분을 선택해주세요.`);
        }

        if (!row.material || row.material.trim() === '') {
          errors.push(`원료를 입력해주세요.`);
        }

        // Mw 또는 Density 중 하나는 필수
        const hasMw = row.mw !== null && row.mw !== undefined && row.mw !== '';
        const hasDensity = row.density !== null && row.density !== undefined && row.density !== '';

        if (!hasMw && !hasDensity) {
          errors.push(`Mw (g/mol) 또는 Density (g/cm3) 중 하나는 반드시 입력해주세요.`);
        }

        // 소수점 검증
        if (hasMw) {
          const mwValue = parseFloat(row.mw);
          if (isNaN(mwValue) || mwValue <= 0) {
            errors.push(`Mw (g/mol) 은 0보다 큰 숫자를 입력해주세요.`);
          } else {
            const mwStr = row.mw.toString();
            if (mwStr.includes('.') && mwStr.split('.')[1].length > 4) {
              errors.push(`Mw (g/mol) 은 소수점 넷째 자리까지만 입력 가능합니다.`);
            }
          }
        }

        if (hasDensity) {
          const densityValue = parseFloat(row.density);
          if (isNaN(densityValue) || densityValue <= 0) {
            errors.push(`Density (g/cm3)는 0보다 큰 숫자를 입력해주세요.`);
          } else {
            const densityStr = row.density.toString();
            if (densityStr.includes('.') && densityStr.split('.')[1].length > 4) {
              errors.push(`Density (g/cm3)는 소수점 넷째 자리까지만 입력 가능합니다.`);
            }
          }
        }
      });

      if (errors.length > 0) {
        this.showPopup(errors.join('\n'));
        return false;
      }

      return true;
    },

    async save() {
      if (this.isFetching) return;

      if (!this.validate()) {
        return;
      }

      this.isFetching = true;

      try {
        const rows = this.$refs.grid.getJsonRows();

        // Account.vue처럼 각 행에 대해 저장 처리
        for (const row of rows) {
          if (row.material && row.material.trim() !== '') {
            await materialPropertyInsert(row);
          }
        }

        this.showPopup('저장이 완료되었습니다.', false, () => {
          this.$emit('closeTab');
          this.$emit('refreshList');
        });
      } catch (error) {
        console.error('저장 실패:', error);
        let errorMessage = '저장 중 오류가 발생했습니다.';

        // 백엔드 에러 메시지 추출
        if (error.response && error.response.data) {
          if (error.response.data.data) {
            errorMessage = error.response.data.data;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.errorMessage) {
            errorMessage = error.response.data.errorMessage;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        }

        this.showPopup(errorMessage);
      } finally {
        this.isFetching = false;
      }
    },

    cancel() {
      this.$emit('closeTab');
    },

    showPopup(text, closable = false, callback) {
      this.alertMessage = text;
      this.alertCallback = callback;
      this.alertDialog = true;
    },

    handleAlertConfirm() {
      this.alertDialog = false;
      if (this.alertCallback && typeof this.alertCallback === 'function') {
        this.alertCallback();
      }
      this.alertCallback = null;
    },
  },

  components: {
    SetPopup,
    RealGrid,
  },
};
</script>

<style scoped>
.service {
  margin: auto;
  width: 100%;
}

.btn-area {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 16px;
}

.btn-reset {
  background-color: #f5f5f5 !important;
  color: #666 !important;
}

.btn-search {
  background-color: #08800a !important;
  color: white !important;
}
</style>
